import React, { useMemo, useCallback, useEffect, useState } from 'react';

import { makeStyles, Container, Grid, Paper } from '@material-ui/core';
import BreadCrumb from 'app/components/BreadCrumb';
import { injectIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import notification from 'app/config/notification';
import { updateRequestDetails, fetchSaveRequest } from 'app/store/slices/requests';
import { setVehicles } from 'app/store/slices/forms';
import { FormServiceContext } from 'app/utils/contexts';
import VehicleRegistration from 'app/components/ServicesForms/Vehicles/VehicleRegistration';
import { generatePath, useHistory } from 'react-router-dom';
import { showLoading, hideLoading } from 'app/store/slices/settings';
import { preparePayload } from 'app/utils/forms/functions';
import { validateVehicleRequest, validateVehicleList } from 'app/utils/forms/validators/vehicle';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import { setVehiclePlateByCompany } from 'app/store/slices/vehicle';

import withRequestInitiation from 'app/components/Hoc/withRequestInitiation';
import alertsEndpoint from 'app/api/services/alertsEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_CODES, SERVICES_PROPERTIES } from 'app/config/services';
import { STATUS_CODES } from 'app/config/status';
import { URLS } from 'app/config/urls';

import styles from './styles';
import { cancelDAE } from 'app/store/slices/daes';

const useStyles = makeStyles(styles);

function Vehicles(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const {
        intl,
        loadingDocuments,
        setLoadingDocuments,
        canEdit,
        editing,
        resetAndGo,
        onCancel,
        validateForm,
        breadCrumb,
        rightInfo,
        handleOnBack,
    } = props;

    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const vehicles = useSelector((state) => state.forms.vehicles);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const userAdmin = useSelector((state) => state.auth.userAdmin);
    const deletedVehicles = useSelector((state) => state.forms.deletedVehicles);

    const [loadingPlates, setLoadingPlates] = useState(false);

    const [canPartialEdit] = useState(() => {
        if (request.cdSituacao === STATUS_CODES.AGUARDANDO_PAGAMENTO) {
            return true;
        }
        return canEdit;
    });

    const isAdd = useMemo(() => {
        return (
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS ||
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS_SEM_CRC
        );
    }, [service.cdServico]);

    const initialValues = useMemo(() => {
        if (editing) {
            const parsed = JSON.parse(request.jsonSolicitacao);
            if (parsed.veiculos) {
                dispatch(setVehicles(parsed.veiculos));
            }
            return parsed;
        }
        if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues) {
            return SERVICES_PROPERTIES[service.cdServico].formInitialValues;
        }
        return {};
    }, [dispatch, editing, request.jsonSolicitacao, service.cdServico]);

    // Busca os primeiros veiculos
    useEffect(() => {
        const init = async () => {
            setLoadingPlates(true);
            try {
                let response;
                response = await vehiclesEndpoint.getVehiclesPlatesByCompany();
                if (response?.data) {
                    dispatch(setVehiclePlateByCompany(response.data));
                }
                if (!isAdd) {
                    if (!response.data || response.data.length <= 0) {
                        if (!request.situacao) {
                            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.NO_VEHICLES_FOUND' }), {
                                toastId: 'MESSAGES.SERVICE.FORMS.NO_VEHICLES_FOUND',
                                type: 'error',
                            });
                            history.push(generatePath(URLS.SERVICES));
                        }
                    }
                }
                // dispatch(fetchVehicleAllPlates());
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            setLoadingPlates(false);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isAdd]);

    const onSave = useCallback(
        async (values, fromSubmit, isInternalForm) => {
            const { cdServico } = service;

            if (
                !canPartialEdit ||
                (cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS &&
                    request.situacao &&
                    !request.situacao.boEdicaoPortalExterno)
            ) {
                return;
            }

            dispatch(showLoading());
            const valuesCopy = {
                ...values,
                veiculos: vehicles,
                situacao: request?.situacao ?? {},
            };

            const validVehicleRequest = validateVehicleRequest({
                values: valuesCopy,
                intl,
                isInternalForm: isInternalForm,
            });
            if (!validVehicleRequest) {
                dispatch(hideLoading());
                return;
            }

            const validVehicleList = await validateVehicleList(intl, cdServico, request.cdSolicitacao, vehicles);
            if (!validVehicleList) {
                dispatch(hideLoading());
                return;
            }

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: { store: documentsStore, files: valuesCopy.documentos },
                params: request?.cdServico ? { ...request } : { cdServico },
                activeCompany,
                userAdmin,
                registrationUserInfo,
            });

            if (deletedVehicles.length > 0) {
                const canceled = await cancelDAE(request.cdSolicitacao, deletedVehicles);
                if (!canceled) {
                    dispatch(hideLoading());
                    return;
                }
            }

            let showMessage = false;
            let response = false;
            if (!fromSubmit) showMessage = true;
            try {
                response = await fetchSaveRequest({
                    isFormData: true,
                    preparedPayload,
                    dispatch,
                    intl,
                    showMessage,
                    cdService: cdServico,
                });
                if (response?.status === 200 && response?.data) {
                    await dispatch(updateRequestDetails(response.data));
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            if (!fromSubmit) dispatch(hideLoading());
            return response;
        },
        [
            activeCompany,
            canPartialEdit,
            deletedVehicles,
            dispatch,
            documentsStore,
            intl,
            registrationUserInfo,
            request,
            service,
            userAdmin,
            vehicles,
        ],
    );

    const onSubmit = useCallback(
        async (values) => {
            if (!canEdit) {
                if (service.cdServico !== SERVICES_CODES.EXCLUSAO_VEICULOS) {
                    return;
                } else {
                    if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
                }
            }

            dispatch(showLoading());

            if (!(await validateVehicleList(intl, service.cdServico, request.cdSolicitacao, vehicles))) {
                dispatch(hideLoading());
                return;
            }

            const currentStatusCode = request.cdSituacao;

            if ((documentsStore.length > 0 && values.documentos?.length === 0) || vehicles.length === 0) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'warning',
                });
                dispatch(hideLoading());
                return false;
            }

            //save before finish
            const responseSave = await onSave(values, true);

            if (responseSave && responseSave.status === 200) {
                try {
                    const response = await vehiclesEndpoint.finishRequest(responseSave.data.cdSolicitacao);
                    if (response.status === 200) {
                        notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }), {
                            toastId: 'SUCCESSFUL_FINISHED_REQUEST',
                            type: 'success',
                        });

                        if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
                            await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
                        }
                    }
                    resetAndGo();
                } catch (error) {
                    errorHandler(error.response?.status, error.response?.data?.message);
                }
            }
            dispatch(hideLoading());
        },
        [
            canEdit,
            dispatch,
            documentsStore.length,
            intl,
            onSave,
            request.cdSituacao,
            request.cdSolicitacao,
            request.situacao,
            resetAndGo,
            service.cdServico,
            vehicles,
        ],
    );

    const contextProvider = useMemo(() => {
        return {
            loadingDocuments,
            setLoadingDocuments,
            validateForm,
            onSubmit,
            onSave,
            onCancel,
            validationSchema: SERVICES_PROPERTIES[service.cdServico]
                ? SERVICES_PROPERTIES[service.cdServico].validationSchema
                : {},
            canEdit,
            loadingPlates: loadingPlates,
        };
    }, [
        loadingDocuments,
        setLoadingDocuments,
        validateForm,
        onSubmit,
        onSave,
        onCancel,
        service.cdServico,
        canEdit,
        loadingPlates,
    ]);

    return (
        <Container>
            <BreadCrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper className={classes.container} elevation={3}>
                        <FormServiceContext.Provider value={contextProvider}>
                            <VehicleRegistration {...props} initialValues={initialValues} />
                        </FormServiceContext.Provider>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(withRequestInitiation(Vehicles));
